import { useClient } from '@based'
import { useEffect, useRef } from 'react'
import type { AnalyticsEventType } from '../../../shared/analyticsEvents.js'
import { useLanguage } from './useLanguage.js'
import { useSession } from './useSession.js'

const THROTTLE_MS = 500

type CachedChannel = {
  channel: ReturnType<ReturnType<typeof useClient>['channel']>
  unsubscribe: () => void
  refCount: number
}

const channelCache = new Map<string, CachedChannel>()

export const useTrack = (pageKey: string) => {
  const client = useClient()
  const [lang] = useLanguage()
  let [session] = useSession()
  if (typeof session === 'string') {
    try {
      session = JSON.parse(session)
    } catch {
      console.warn('Session is not valid JSON')
      session = null
    }
  }
  const lastFiredRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    if (!pageKey) return
    let cached = channelCache.get(pageKey)
    if (!cached) {
      const channel = client.channel('track', pageKey)
      const unsubscribe = channel.subscribe(() => {})
      cached = { channel, unsubscribe, refCount: 0 }
      channelCache.set(pageKey, cached)
    }
    cached.refCount++
    return () => {
      const entry = channelCache.get(pageKey)
      if (!entry) return
      entry.refCount--
      if (entry.refCount <= 0) {
        entry.unsubscribe()
        channelCache.delete(pageKey)
      }
    }
  }, [pageKey, client])

  return (eventType: AnalyticsEventType, target?: string) => {
    const cached = pageKey ? channelCache.get(pageKey) : null
    if (!cached) return
    const now = Date.now()
    const last = lastFiredRef.current.get(eventType) ?? 0
    if (now - last < THROTTLE_MS) return
    lastFiredRef.current.set(eventType, now)
    try {
      cached.channel.publish({
        eventType,
        lang: lang.code,
        ...(session ? { session } : {}),
        target,
      })
    } catch {}
  }
}
